import { makeAutoObservable } from 'mobx';
import { Icon, Label, Milestone, RawProjectTemplate } from '../interfaces/project';
import { StatusListInterface } from '../../interfaces/ProjectInterface';
import { createObservableTask, TaskModel } from './task';
import { rootStore } from '../rootStore';
import { TaskGroupInterface } from '../../interfaces/TaskInterface';
import { formatTemplateTasks } from '../../services/mobxTaskFilterService';
import { RawTask } from '../interfaces/task';
import { genericUpsert } from '../common';
import { getColorIndex } from '../../helpers/common';
import { PROJECT_ICON_TYPE, TaskFilterPropertyType } from '../../global/constants';

export class ProjectTemplateModel {
  id: string;
  name: string;
  description: string;
  createdBy: string;
  createdTime: Date;
  updatedBy: string;
  updatedTime: Date | null;
  updated: boolean = false;
  isArchived: boolean = false;
  value: string;
  companyId: string;

  isEstimate: boolean = false;
  isMilestone: boolean = false;
  multiAssignee?: boolean = false;
  priority: {
    enabled: boolean;
    default: number;
  };
  statusData: StatusListInterface[];
  statusEnable: boolean = false;
  labelsEnabled: boolean = false;
  labelsList: Label[] = [];
  isGroupEnabled: boolean = false;
  taskGroups: TaskGroupInterface[] = [];
  milestones: Milestone[] = [];
  icon: Icon | null = null;
  tasks: TaskModel[] = [];
  userIds: string[] = [];
  listItemsDisplayFilter: Partial<Record<TaskFilterPropertyType, boolean>> & { [key: string]: boolean } = {};

  constructor(rawProjectTemplate: RawProjectTemplate) {
    makeAutoObservable(this);

    this.id = rawProjectTemplate.id;
    this.name = rawProjectTemplate.name;
    this.description = rawProjectTemplate.description;
    this.createdBy = rawProjectTemplate.createdBy;
    this.createdTime = new Date(rawProjectTemplate.createdTime);
    this.updatedBy = rawProjectTemplate.updatedBy;
    this.companyId = rawProjectTemplate.companyId;
    this.updatedTime = rawProjectTemplate.updatedTime ? new Date(rawProjectTemplate.updatedTime) : null;
    this.updated = false;
    this.isArchived = rawProjectTemplate.isArchived;
    this.value = rawProjectTemplate.value || '';
    this.isEstimate = rawProjectTemplate.isEstimate;
    this.isMilestone = rawProjectTemplate.isMilestone;
    this.multiAssignee = rawProjectTemplate.multiAssignee;
    this.priority = rawProjectTemplate.priority;
    this.statusData = rawProjectTemplate.statusData;
    this.statusEnable = rawProjectTemplate.statusEnable;
    this.labelsEnabled = rawProjectTemplate.labelsEnabled;
    this.labelsList = rawProjectTemplate.labelsList;
    this.multiAssignee = rawProjectTemplate.multiAssignee;
    this.isGroupEnabled = rawProjectTemplate.isGroupEnabled;
    this.taskGroups = rawProjectTemplate.groups;
    this.tasks = rawProjectTemplate.tasks.map(createObservableTask);
    this.userIds = rawProjectTemplate.userIds || [];
    this.icon = {
      type: rawProjectTemplate?.icon?.type || PROJECT_ICON_TYPE.DEFAULT,
      reference: rawProjectTemplate?.icon?.reference,
      color: rawProjectTemplate?.icon?.color || getColorIndex(rawProjectTemplate?.name)
    };
    this.milestones = [];
  }

  get defaultStatus() {
    return this.statusData.find((status) => status.Default);
  }

  updateListItemsDisplayFilter(propertyType: string, isVisible: boolean) {
    this.listItemsDisplayFilter[propertyType] = isVisible;
  }

  isPropertyVisible(propertyType: string): boolean {
    return this.listItemsDisplayFilter[propertyType] !== false;
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users.filter((user) => this.userIds.includes(user.id)) || [];
  }

  get filteredProjectTemplateTasks() {
    return formatTemplateTasks(this.tasks, this);
  }

  setIcon(icon: Icon) {
    this.icon = icon;
    this.updated = true;
  }

  setUsersIds(userIds: string[]) {
    this.userIds = userIds;
    this.updated = true;
  }

  setGroupEnabled(isGroupEnabled: boolean) {
    this.isGroupEnabled = isGroupEnabled;
    this.updated = true;
  }

  setPriorityEnabled(priorityEnabled: boolean) {
    this.priority.enabled = priorityEnabled;
    this.updated = true;
  }

  setDefaultPriority(defaultPriority: number) {
    this.priority.default = defaultPriority;
    this.updated = true;
  }

  setStatusEnabled(statusEnabled: boolean) {
    this.statusEnable = statusEnabled;
    this.updated = true;
  }

  setLabelsEnabled(labelsEnabled: boolean) {
    this.labelsEnabled = labelsEnabled;
    this.updated = true;
  }

  setLabelsList(labelsList: Label[]) {
    this.labelsList = labelsList;
    this.updated = true;
  }

  setStatusData(statusData: StatusListInterface[]) {
    this.statusData = statusData;
    this.updated = true;
  }

  setTaskGroups(groups: TaskGroupInterface[]) {
    this.taskGroups = groups;
    this.updated = true;
  }

  setMultiAssignee(multiAssignee: boolean) {
    this.multiAssignee = multiAssignee;
    this.updated = true;
  }

  setEstimate(isEstimate: boolean) {
    this.isEstimate = isEstimate;
    this.updated = true;
  }

  setMilestone(isMilestone: boolean) {
    this.isMilestone = isMilestone;
    this.updated = true;
  }

  setUpdated(updated: boolean) {
    this.updated = updated;
  }

  setTasks(tasks: TaskModel[]) {
    this.tasks = tasks;
    this.updated = true;
  }

  addTask(task: RawTask) {
    this.tasks.push(createObservableTask(task));
    this.updated = true;
  }

  upsertTasks(rawTasks: RawTask[]) {
    this.updated = true;
    // Keep ordering and comments from the existing task, but let 'files' update
    // so adding or removing an attachment while editing is saved.
    const { newItems, updatedItems } = genericUpsert(this.tasks, rawTasks, createObservableTask, {
      strategy: 'merge',
      excludeFields: ['index', 'subtaskIndex', 'taskNumber', 'comments']
    });
    return { newItems, updatedItems };
  }
}

export const createProjectTemplateModel = (rawProjectTemplate: RawProjectTemplate) => {
  return new ProjectTemplateModel(rawProjectTemplate);
};
